import { Injectable } from '@angular/core';
import { PortColFormatterComponent } from '@irb-common/components/irebal-ag-grid-ent-avatar/portcolformatter.component';
import { BlendedSecurityAllocationGroupcellComponent } from '@irb/irb-editors/editor-blended-model/blended-security-allocation/blended-security-allocation-groupcell.component';
import { SecurityMasterAssetCategoryService } from '@irb/irb-models/security-master/security-master.asset-category.service';
import { DropdownSearchMultipleRendererComponent } from '@irb-common/components/grid/renderers/dropdown-search-multiple.cell.component';
import { BlendedSecurityAllocationComponent } from './blended-security-allocation.component';
import { SacSecurityTypeService } from '@irb/irb-models/security-master/sac-security-type.service';

@Injectable({
    providedIn: "root"
})
export class BlendedSecurityAllocationService {
    constructor(private smac: SecurityMasterAssetCategoryService, private ssts: SacSecurityTypeService) {}

    getFrameworkComponents() {
        return {
            "cellRendererFormatter": PortColFormatterComponent,
            "BlendedSecurityAllocationGroupcellComponent": BlendedSecurityAllocationGroupcellComponent,
            DropdownSearchMultipleRendererComponent: DropdownSearchMultipleRendererComponent
        };
    }

    getDetailFrameworkComponents() {
        return {
            "cellRendererFormatter": PortColFormatterComponent
        }
    }

    getSecAllocColumnDefs(parent: BlendedSecurityAllocationComponent) {
        let cols: any = [
            {
                headerName: "Securities",
                children: [
                    {
                        headerName: "Symbol",
                        field: "securitySymbol",
                        sort: "asc",
                        showRowGroup: true,
                        cellRenderer: 'agGroupCellRenderer',
                        cellRendererParams: {
                            suppressCount: true,
                            innerRenderer: 'BlendedSecurityAllocationGroupcellComponent',
                            suppressDoubleClickExpand: true,
                            suppressEnterExpand: true,
                        },
                        lockVisible: true,
                        minWidth: 130,
                        lockPosition: true,
                        colId: 'securitySymbol',
                        cellClass: (params: any) => {
                            if (params.data.conflict === true) {
                                return 'allocation-conflict'
                            }
                        }
                    },
                    {
                        headerName: "Description",
                        field: "securityDescription",
                        minWidth: 180,
                        cellRenderer: 'cellRendererFormatter',
                        colId: 'securityDescription'
                    },
                    {
                        headerName: parent.ENABLE_AS_SECTYPE ? 'Trade Type' : 'Type',
                        field: "securityType",
                        minWidth: 80,
                        cellRendererSelector: this.formatSecurityTypeSelector,
                        cellRenderer: 'cellRendererFormatter',
                        colId: 'securityType'
                    },
                    {
                        headerName: "CustCat",
                        field: "customCategoryName",
                        minWidth: 130,
                        cellRenderer: 'colorCategoryComponent',
                        colId: 'customCategoryName'
                    },
                    {
                        headerName: "NER",
                        field: "netExpenseRatio",
                        headerClass: 'editHeaderSecAllPercent',
                        minWidth: 50,
                        hide : true,
                        suppressFiltersToolPanel: true,
                        cellRendererSelector: (params: any) => {
                                if (params.data) {
                                  if (params.data.netExpenseRatio === null || params.data.netExpenseRatio === 0) {
                                    return {
                                        component: 'cellRendererFormatter',
                                        params: { value: 'n/a' },
                                      };
                                  } else {
                                      return{
                                        component: 'cellRendererFormatter',
                                        params: { value:  params.data.netExpenseRatio + " %"} 
                                      } 
                                  } 
                          }
                        }
                    },
                    {
                        headerName: "Weighted Target %",
                        field: "weightedTarget",
                        headerClass: 'editHeaderSecAllPercent',
                        lockVisible: true,
                        minWidth: 70,
                        type: 'numericColumn',
                        colId: 'weightedTarget',
                        comparator: (valueA: string, valueB: string) => {
                            const numA = valueA ? Number(valueA) : 0;
                            const numB = valueB? Number(valueB) : 0;
                            if (numA === numB) {
                                return 0;
                            }
                            return numA > numB ? 1 : -1;
                        }
                    }
                ]
            }];

                cols.push({
                    headerName: 'Schwab Asset Classes',
                    children: [
                        {
                            headerName: "Level 1",
                            field: "acLevel1",
                            width: 250,
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel1),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    showNone: (!params.data.parentChildId) ? true : false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 1",
                                    hiddenLabelDescription: "classes"
                                };
                            }
                        },
                        {
                            headerName: "Level 2",
                            field: "acLevel2",
                            width: 250,
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel2),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    showNone: (!params.data.parentChildId) ? true : false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 2",
                                    hiddenLabelDescription: "classes"
                                };
                            },
                            hide: this.getDefaultHiddenState(parent, 'acLevel2', true)
                        },
                        {
                            headerName: "Level 3",
                            field: "acLevel3",
                            width: 250,
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel3),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    showNone: (!params.data.parentChildId) ? true : false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 3",
                                    hiddenLabelDescription: "classes"
                                };
                            },
                            hide: this.getDefaultHiddenState(parent, 'acLevel3', true)
                        },
                        {
                            headerName: "Level 4",
                            field: "acLevel4",
                            width: 250,
                            minWidth: 160,
                            flex: 1,
                            suppressFiltersToolPanel: true,
                            sortable: false,
                            cellClass: 'dropdownSearchMultipleCell',
                            cellRenderer: 'DropdownSearchMultipleRendererComponent',
                            cellRendererParams: (params: any) => {
                                return {
                                    defaultValue: this.smac.getCellDisplayForAssetClasses(params.data.acLevel4),
                                    noneDisplayText: params.data?.securitySymbol === '$CASH' ? 'Cash' : 'none',
                                    cellStyle: "classes",
                                    canEdit: false,
                                    showNone: (!params.data.parentChildId) ? true : false,
                                    hiddenLabelPanelTitle: "Schwab Asset Classes - Level 4",
                                    hiddenLabelDescription: "classes"
                                };
                            },
                            hide: this.getDefaultHiddenState(parent, 'acLevel4', true)
                        }
                    ]
                });

            cols.push({
                headerName: "Rebalance Bands",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.rebalanceBands()
                },
                children: [
                    {
                        headerName: "Use Default Settings",
                        headerComponentParams: {
                            irbColInfo: this.useDefault()
                        },
                        field: "useGlobalValue",
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true},
                        lockVisible: true,
                        minWidth: 60,
                        colId: 'useGlobalValue'
                    },
                    {
                        headerName: "Min %",
                        field: "toleranceMinPercentageWT",
                        minWidth: 70,
                        valueFormatter: (params: any) => {
                            return Number(params.value).toFixed(2);
                        },
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        colId: 'toleranceMinPercentageWT'
                    },
                    {
                        headerName: "Max %",
                        field: "toleranceMaxPercentageWT",
                        valueFormatter: (params: any) => {
                            return Number(params.value).toFixed(2);
                        },
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        minWidth: 70,
                        colId: 'toleranceMaxPercentageWT'
                    }
                ]
            },
            {
                headerName: "Trading Rules",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.tradeRules()
                },
                children: [
                    {
                        headerName: "Use Sec Master",
                        headerComponentParams: {
                            irbColInfo: this.useSecMaster()
                        },
                        field: "useSecurityValue",
                        lockVisible: true,
                        minWidth: 70,
                        cellRenderer: 'cellRendererFormatter',
                        cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true},
                        colId: 'useSecurityValue'
                    },
                    {
                        headerName: "Min Buy $",
                        field: "buyMinimum",
                        hide : true,
                        minWidth: 90,
                        colId: 'buyMinimum',
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        valueGetter: (params: any) => {
                            return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.buyMinimum ? params.data.buyMinimum : '0';
                        }
                    },
                    {
                        headerName: "Min Sell $",
                        field: "sellMinimum",
                        hide : true,
                        minWidth: 90,
                        colId: 'sellMinimum',
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        valueGetter: (params: any) => {
                            return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.sellMinimum ? params.data.sellMinimum : '0';
                        }
                    },
                    {
                        headerName: "Min Buy %",
                        field: "buyMinimumPercent",
                        hide : true,
                        minWidth: 70,
                        colId: 'buyMinimumPercent',
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        valueGetter: (params: any) => {
                            return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.buyMinimumPercent ? params.data.buyMinimumPercent +'%' : '0.00%';
                        }
                    },
                    {
                        headerName: "Min Sell %",
                        field: "sellMinimumPercent",
                        hide : true,
                        minWidth: 70,
                        colId: 'sellMinimumPercent',
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        valueGetter: (params: any) => {
                            return params.data.securitySymbol === '$CASH' ? 'n/a' : params.data.sellMinimumPercent ? params.data.sellMinimumPercent +'%' : '0.00%';
                        }
                    }
                ]
            },
            {
                headerName: "Restrictions",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.restrictions()
                },
                children: [
                    {
                        headerName: "Restriction Type",
                        field: "sellingTreatment",
                        hide : true,
                        cellRenderer: 'cellRendererFormatter',
                        minWidth: 120,
                        colId: 'sellingTreatment',
                        valueGetter: (params: any) => {
                            let formatRestriction = (item: string) => {
                                switch(item) {
                                    case "NEVER_SELL": return "Never Sell";
                                    case "NO_RESTRICTION": return "No restriction";
                                    case "NEVER_BUY": return "Never Buy";
                                    case "NEVER_BUY_AND_SELL": return "Never Buy and Never Sell";
                                    case "UN_MANAGED": return "Exclude";
                                    default : return  "";
                                }
                            };
                            return params.data.securitySymbol === '$CASH' ? 'n/a' : formatRestriction(params.data.sellingTreatment);
                        }
                    },
                    {
                        headerName: "Action to Take",
                        field: "conditions",
                        hide : true,
                        minWidth: 95,
                        cellRenderer: this.formatRestrictionCondition,
                        colId: 'conditions'
                    }
                ]
            },
            {
                headerName: "Equivalents & Alternatives",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.equivalentsAndAlt()
                },
                children: [
                    {
                        headerName: "# Equivs",
                        field: "equivalenceQty",
                        hide : true,
                        minWidth: 80,
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        headerComponentParams: {
                            irbColInfo: this.equivs()
                        },
                        colId: 'equivalenceQty',
                        valueGetter: (params: any) => {
                            return params.data.equivalenceQty ? params.data.equivalenceQty : '0';
                        }
                    },
                    {
                        headerName: "Alt if Held",
                        field: "altIfHeldSymbol",
                        hide : true,
                        minWidth: 100,
                        headerComponentParams: {
                            irbColInfo: this.altAfHeld()
                        },
                        cellRenderer : "cellRendererFormatter",
                        colId: 'altIfHeldSymbol',
                        valueGetter: (params: any) => {
                            return params.data.altIfHeldSymbol ? params.data.altIfHeldSymbol : 'none';
                        }
                    },
                    {
                        headerName: "Min Alt",
                        field: "tradeMinAltName",
                        hide : true,
                        minWidth: 100,
                        headerComponentParams: {
                            irbColInfo: this.minAlt()
                        },
                        colId: 'tradeMinAltName',
                        valueGetter: (params: any) => {
                            return params.data.tradeMinAltName ? params.data.tradeMinAltName : 'none';
                        }
                    },
                    {
                        headerName: "Alt Sec",
                        field: "taxAltSymbolName",
                        hide : true,
                        minWidth: 100,
                        headerComponentParams: {
                            irbColInfo: this.altSec()
                        },
                        cellRenderer : "cellRendererFormatter",
                        colId: 'taxAltSymbolName',
                        valueGetter: (params: any) => {
                            return params.data.taxAltSymbolName ? params.data.taxAltSymbolName : 'none';
                        }
                    },
                    {
                        headerName: "Alt Acct",
                        field: "inverseAccType",
                        hide : true,
                        minWidth: 100,
                        headerComponentParams: {
                            irbColInfo: this.altAcct()
                        },
                        colId: 'inverseAccType',
                        valueGetter: (params: any) => {
                            let inData = params.data.inverseAccType;
                            if (inData === "QUALIFIED") {
                                inData = "Qualified";
                            } else if (inData === "TAXABLE") {
                                inData = "Taxable";
                            }
                            return inData;
                        }
                    }
                ]
            },
            {
                headerName: "Household Location Prefs",
                children: [
                    {
                        headerName: "Level",
                        field: "taxabilityBias",
                        hide : true,
                        minWidth: 70,
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        headerComponentParams: {
                            irbColInfo: this.locationLevel()
                        },
                        colId: 'taxabilityBias',
                        valueGetter: (params: any) => {
                            return params.data.taxabilityBias ? params.data.taxabilityBias : '0';
                        }
                    },
                    {
                        headerName: "Roth Level",
                        field: "rothLevel",
                        hide : true,
                        minWidth: 90,
                        type: 'numericColumn',
                        headerClass: 'not-right-align',
                        headerComponentParams: {
                            irbColInfo: this.locationRothLevel()
                        },
                        colId: 'rothLevel',
                        valueGetter: (params: any) => {
                            return params.data.rothLevel ? params.data.rothLevel : '0';
                        }
                    }
                ]
            });

        if (parent.ENABLE_AS_SECTYPE) {
            cols[0].children.splice(3, 0, {
                headerName: 'Security Type',
                colId: 'sacSecurityType',
                field: 'sacSecurityType',
                minWidth: 80,
                flex: 1,
                filter: 'irbAgSearchMultipleColumnFilterComponent',
                suppressFiltersToolPanel: true,
                floatingFilterComponent: 'irbInputFloatingFilterComponent',
                filterParams: (_params: any) => {
                    return {
                        values: this.ssts.getDropdownOptionsForSacSecurityTypes(),
                        enableHiddenSearchResults: false,
                        enableEmptySearchTerm: true
                    };
                }
            });

            cols[0].children.splice(8, 0, {
                headerName: "Morningstar Stock Style",
                field: 'styleBoxLabel',
                flex: 1
            });
        }

        return cols;
    }

    getTlhWsaColumns(tlhWashSaleSymbol: any) {
        return [
            {
                headerName: 'Tax Loss Harvest',
                field: 'isTlhEnabled',
                minWidth:85,
                valueGetter: (params: any) => {
                    let returnValue = '';
                    switch(params.data.isTlhEnabled) {
                        case true:
                            returnValue = 'On';
                            break;
                        case false:
                            returnValue = 'Off'
                            break;
                    }
                    returnValue = !tlhWashSaleSymbol ? '' : returnValue;
                    if (params.data && params.data.securitySymbol === "$CASH")
                      returnValue = 'n/a';
                    return returnValue;
                },
            },
            {
                headerName: "Wash sale alternate",
                field: "tlhWashSaleSymbol",
                minWidth: 100,
                cellRenderer : "cellRendererFormatter",
                valueGetter: (params: any) => {
                    let retVal = params.data.tlhWashSaleSymbol ? params.data.tlhWashSaleSymbol : 'none';
                    if (retVal === 'None') {
                        retVal = 'none';
                    }
                    if (params.data && params.data.securitySymbol === "$CASH")
                      retVal = 'n/a';
                    return retVal;
                }
            }
        ]
    }
    formatSecurityTypeSelector = (params:any) => {
        if (params.data) {
            return {
                component: 'cellRendererFormatter',
                params: {
                    value: this.formatSecurityType(params)
                }
            };
        }
    }
    formatSecurityType = (params:any) => {
        switch (params.data?.securityType) {
            case "EQUITY":
                return "Equity";
            case "EQ":
                return "Equity";
            case "MUTUAL_FUND":
                return "Mutual Fund";
            case "MF":
                return "Mutual Fund";
            case "CASH":
                return "Cash";
            case "GROUP":
                return "Group";
            case "BOND":
                return "Bond";
            case "OTHER":
                return "Other";
            case "EXTERNAL":
                return "External";
            case "CustCat":
                return "Category";
            case "OPTION":
                return "Option";
            default:
                return params.data?.securityType;
        }
    }

    useDefault() {
        return 'When this is checked, the underlying security model is following the global settings for rebalancing bands. When it\'s not checked, the rebalancing bands were overridden within the security model.';
    }
    rebalanceBands() {
        let msg= "<p>The minimum and maximum rebalancing band is automatically calculated for each model security using the values set in your global settings.</p>";
        msg += "<p>You can override the calculated bands for any security by deselecting the use auto checkbox and entering new values.</p>";
        return msg;
    }
    tradeRules() {
        let msg = '<p><b>Min Buy</b> and <b>Min Sell</b> are your preferred minimum trade amounts for any proposed trades of the security. </p>';

        msg += '<p><b>Note:</b>&nbsp;For mutual funds, if you set both a dollar and a percent minimum, they will be compared to the applicable fund minimums, ' +
            'and the highest of these values will take precedence<span id="useSecMasterInfoVideoHldr"></span></p>';
        return msg;
    }
    useSecMaster() {
        let msg = '<p>When this is checked, all usage rules will follow your global settings. When it\'s not checked, those global settings will be overridden with the values you enter here.<span id="useSecMasterInfoVideoHldr"></span></p>';
        return msg;
    }
    restrictions() {
        return '<p>A security <b>restriction</b> is a trading rule that will be applied to all portfolios following this model unless it\'s overridden within the individual portfolio.</p>';
    }
    equivalentsAndAlt() {
        const msg = "<p>The <b>Asset Location Level</b> is a number assigned to each security that represents whether you want the security to be located in a qualified account or a taxable account. This only applies if you are rebalancing at the household level, with portfolios containing both taxabl and qualified accounts. " +
            "When a rebalance runs, it will place the securities with the highest absolute value numbers into the desired accounts first, and place those with numbers nearest to zero last. The result is a greater chance of getting the securities marked as -100 into taxable accounts and closest to +100 into qualified accounts. "+
            "The <b>Taxable Alternate</b> is a security that will be bought when rebalancing if the subject security is placed in the wrong (or not desired) account type. "+
            "If an alternate security is selected, you will also have to specify the <b>Account type</b> in which you want to buy the alternate security.</p>";
        return msg;
    }
    locationLevel() {
        let msg = "<div>The asset location <b>level</b> is a number assigned to each security that represents your relative preference for each security to be purchased in either " +
            "a qualified account or a taxable account. +100 represents the highest tax impact, and therefore the greatest preference for qualified account placement, " +
            "while -100 represents the lowest tax impact, and therefore the greatest preference for taxable account placement.</div>";

        msg += "<br /><div>During rebalancing, iRebal will identify securities with the highest absolute value of location <b>level</b> and will attempt to place those securities " +
            "into the desired accounts first. Securities marked closer to -100 and +100 will have a greater likelihood of being placed into the appropriate taxable and qualified accounts. " +
            "Note: When locating into a qualified account, <b>level</b> does not make a distinction between qualified and Roth accounts.</div>";

        msg +="<br/><div>If a security is already held in the portfolio and you prefer that subsequent purchases of the security are placed in the account where it is already held, enter a location level of between -49 to +49 for the security.</div>";
        return msg;
    }
    locationRothLevel() {
        let msg = "<div>The <strong>Roth level</strong> is a number  assigned to securities identifying a preference to be bought in a Roth account. Numbers may range from +100 to 0, with +100 representing greatest preference to locate into a Roth account relative to other securities with a lower <b>Roth level</b>.</div>";

        msg += "<br /><div>During rebalancing, securities with a defined <b>Roth level</b> have the highest priority of placement, and will be located first based on the securities with the highest <b>Roth level</b> value. In absence of a defined <b>Roth level</b>, iRebal will locate securities based on location <b>level</b>, and will not make a distinction between qualified and Roth accounts.</div>";
        return msg;
    }
    minAlt() {
        return '<b>Min Alt</b> is your optional selection of a similar substitute security to buy if a trade smaller than the Min Buy amount is proposed. Any holdings of the Min Alt will be treated as an equivalent.';
    }
    equivs() {
        let msg = "<b>Equivalents</b> are securities that, when held, will be treated as an acceptable substitute for the parent security. ";
        msg += "Holdings in equivalents are counted toward the allocation of the parent security when calculating a portfolio's allocations compared to ";
        msg += "target. Equivalent securities will not be purchased, but can be held and sold in place of the parent security.";
        return msg;
    }
    altAfHeld() {
        let msg = "<p><b>Alt if Held</b> securities are those you'd like iRebal to purchase instead of a specific security when the <b>Alt if Held</b> ";
        msg += "is already in the account. If you specify multiple alternates, iRebal will purchase whichever one there's more of in the account.</p>";
        msg += "<p><b>Example:</b> An alternate might be specified in the case of a fund that's closed to new investors: Since subsequent future purchases  ";
        msg += "of the closed fund are sometimes allowed, the <b>Alt if Held</b> fund will be purchased when the fund is held; the new fund will only be  ";
        msg += "purchased for initial investors.</p>";
        return msg;
    }
    altSec() {
        return 'These settings tell iRebal to purchase an alternate security which will be used in place of this security when trading in a certain account type. For example, if the subject security is a corporate bond fund, and you\'d like to use a municipal bond fund in its place in taxable accounts, enter the symbol for the municipal bond fund in the Alt Sec field and select Taxable as the Alt Acct.';
    }
    altAcct() {
        return 'These settings tell iRebal to purchase an alternate security which will be used in place of this security when trading in a certain account type. For example, if the subject security is a corporate bond fund, and you\'d like to use a municipal bond fund in its place in taxable accounts, enter the symbol for the municipal bond fund in the Alt Sec field and select Taxable as the Alt Acct.';
    }
    formatRestrictionCondition(params: any) {
        if (params.data.sellingTreatment === 'NEVER_BUY' || params.data.sellingTreatment === 'NEVER_BUY_AND_SELL') {
            switch (params.data.conditions) {
                case "Leave in cash":
                    return "Leave in Cash";
                case "Buy an alternate security":
                    return params.data.altSecuritySymbol;
                case "Pro-rate across other model securities":
                    return "Pro-rate";
                default :
                    return "";
            }
        }
    }

    getDefaultHiddenState(parent: any, fieldName: string, defaultState: boolean) {
      let s: any = JSON.parse(window.localStorage.getItem(parent.componentName) || "{}");

      if(s.COL_STATE) {
          let c: any  = JSON.parse(s.COL_STATE);

          if(c) {
              let d = c.find((x: any) => x.colId == fieldName);
              return d?.hide;
          }
      }

      return defaultState;
    }
}
